<template>
    <Popup :open="open" @open="$emit('update:open', (open = !open))" popupWidth="w-3/4 md:w-3/4 lg:w-2/3" :padding="true">
        <div class="flex flex-col max-h-[90vh]">
            <!-- Scrollable Content -->
            <Loading :loading="loading" class="flex-1 overflow-y-auto p-6">
                <div v-if="!loading" class="space-y-6">
                    <!-- Header -->
                    <div class="border-b border-gray-300 pb-4">
                        <h2 class="text-lg font-bold">
                            {{ $t('subscription_updates') + ': ' + abonnement.childProducts[0].parentName }}
                        </h2>
                    </div>

                    <!-- Current Products Grid -->
                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        <div v-for="child in abonnement.childProducts" :key="child.id" class="flex flex-col gap-2 p-4 border border-gray-200 rounded-lg shadow-sm bg-white">
                            <div class="flex items-center gap-4">
                                <div class="w-[50px] h-[50px] rounded-md overflow-hidden">
                                    <img :alt="$translate(child.product.name)" class="w-full h-full object-contain" :src="child.product.image.replace('.png', '-gradient.png')" />
                                </div>
                                <strong class="text-sm">{{ $translate(child.product.name) }}</strong>
                            </div>

                            <div class="text-sm mt-2 space-y-1">
                                <div>
                                    <span class="font-semibold">{{ $t('current_runtime') }}: </span>
                                    {{ runtimeText(child.runtimeInMonths) }}
                                </div>
                                <div class="text-blue text-xs">
                                    {{
                                        $t('current_update_credit_discount_checkout', {
                                            discount: $toCurrency(child.remainingGrossPrice)
                                        })
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Upgrade Product -->
                    <div class="flex flex-col gap-4 border-t border-gray-300 pt-4">
                        <div class="flex items-center gap-4">
                            <div class="w-[50px] h-[50px] rounded-md overflow-hidden">
                                <img
                                    :alt="$translate(abonnement.upgradeProduct.name)"
                                    class="w-full h-full object-contain"
                                    :src="abonnement.upgradeProduct.image.replace('.png', '-gradient.png')"
                                />
                            </div>
                            <strong class="text-base">{{ $translate(abonnement.upgradeProduct.name) }}</strong>
                        </div>

                        <div v-if="runtimeOptions.length">
                            <strong class="block mb-2">{{ $t('runtime_new') }}</strong>
                            <Radio
                                name="runtime"
                                :formComponentId="$.uid"
                                :label="$t('runtime_choose')"
                                v-model:selected="selectedRuntimeId"
                                :options="runtimeOptions"
                                :multiple="false"
                            />
                        </div>
                    </div>
                </div>
            </Loading>

            <!-- Sticky Footer -->
            <div class="border-t border-gray-300 bg-white shadow-inner sticky bottom-0 z-10">
                <FormButton
                    @click="addRuntimeToCart()"
                    styling="primary flex items-center gap-3 w-full justify-center"
                    :value="$t('upgrade_to') + ' ' + $translate(abonnement.upgradeProduct.name)"
                    :disabled="false"
                />
            </div>
        </div>
    </Popup>
</template>

<script>
import { mapGetters } from 'vuex';
import Toastification from '../miscellaneous/Toastification.vue';
import _ from 'lodash';
import i18next from 'i18next';

export default {
    data: () => ({
        runtimeOptions: [],
        selectedRuntimeId: null,
        loading: true,
        abonnement: null
    }),

    mounted() {
        this.loading = true;

        this.$store.dispatch('abonnements/upgradeProducts', this.abonnementId).then((response) => {
            this.abonnement = response;

            this.abonnement.upgradeVariants.forEach((productVariant) => {
                let periodTime = productVariant.runTimeInMonth;

                this.runtimeOptions.push({
                    label: `${this.runtimeText(periodTime)} <b>${this.$toCurrency(productVariant.totalEndPriceGrossPrice)}</b><br><small> ${i18next.t('runtime_discount')} ${this.$toPercent(productVariant.discountPercent)}</small>`,
                    value: productVariant.id,
                    sublabel: productVariant.totalEndPriceGrossPrice
                });
            });

            // set the first runtime option as the selected
            this.selectedRuntimeId = this.runtimeOptions[0].value;

            this.loading = false;

            // reload the abonnements in parent component
            this.$parent.indexAbonnements();
        });
    },

    props: ['open', 'abonnementId'],
    emits: ['update:open'],

    watch: {
        open: function () {
            this.$emit('update:open', this.open);
        }
    },
    computed: {
        background() {
            const backgroundColor = this.abonnement?.product?.productGroups.data[0].productCategories.data[0].background_color;
            const backgroundGradient = this.abonnement?.product?.productGroups.data[0].productCategories.data[0].background_gradient;

            return 'linear-gradient(to bottom, ' + backgroundColor + ' 0%, ' + backgroundGradient + ' 100%)';
        },
        ...mapGetters('payment_methods', {
            getPaymentMethods: 'get'
        }),
        ...mapGetters('user', ['isLoggedIn', 'receivesNewsletters', 'hasCustomer']),
        selectedRuntime() {
            return this.abonnement.parentProductVariantsForUpgrade.data.find((productVariant) => productVariant.id === this.selectedRuntimeId);
        }
    },

    methods: {
        runtimeText(runtime) {
            return parseInt(runtime) === 1 ? i18next.t('count_month', { count: runtime }) : i18next.t('count_month_plural', { count: runtime });
        },

        addRuntimeToCart() {
            this.$store
                .dispatch('cart_products/store', {
                    product_variant_id: this.selectedRuntimeId
                })
                .then(async (response) => {
                    // if no message in response the product was added succesfully
                    if (response?.meta?.message) {
                        _.forEach(response?.meta?.message, (message, type) => {
                            this.$toast[type === 'danger' ? 'error' : type]({
                                component: Toastification,
                                props: {
                                    message: message,
                                    type: type === 'danger' ? 'error' : type
                                }
                            });
                        });
                    } else {
                        this.$store.dispatch('cart/index');
                        this.$router.push({ name: 'checkout' });
                        this.$emit('update:open', false);
                    }
                });
        }
    }
};
</script>

<style scoped>
.icon {
    background: v-bind(background);
}
</style>
